import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import invoiceService from '../../api/services/invoiceService';
import api from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Factures, { formatMoney } from './Factures';
import FactureModal from './FactureModal';
import InvoiceTemplateSettingsModal from './InvoiceTemplateSettingsModal';
import { fetchBranding, loadLocalBranding } from './branding';

// Extrait l'identifiant de conversation WhatsApp d'une facture, quel que soit le champ
const getConversationId = (inv) =>
  inv?.conversationId || inv?.metadata?.conversationId || inv?.conversation?._id || null;

const getPaymentLink = (inv) =>
  inv?.paymentLink || inv?.paymentUrl || inv?.payment?.link || inv?.payment?.url || '';

export default function FacturesConnected() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [busyId, setBusyId] = useState(null);

  // Modale création / édition
  const [showModal, setShowModal] = useState(false);
  const [modalInvoice, setModalInvoice] = useState(null); // null = création

  // Modèle de facture GLOBAL de l'entreprise (logo, design, couleur, société)
  const [branding, setBranding] = useState(() => loadLocalBranding());
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Charge le modèle global (backend prioritaire, repli sur cache local)
  useEffect(() => {
    let active = true;
    fetchBranding().then((b) => { if (active) setBranding(b); });
    return () => { active = false; };
  }, []);

  // ── Chargement via React Query ──────────────────────────────────────────────
  const { data: invoicesData, isLoading: loading, isError, error, isFetching } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => invoiceService.getInvoices({ limit: 1000 }),
  });

  const invoices = useMemo(() => {
    if (invoicesData?.success && invoicesData.data) return Array.isArray(invoicesData.data) ? invoicesData.data : [];
    if (Array.isArray(invoicesData)) return invoicesData;
    return [];
  }, [invoicesData]);

  // ── Mise à jour locale après une action ──────────────────────────────────────
  const applyLocalUpdate = useCallback((updated) => {
    if (!updated?._id) return;
    qc.setQueryData(['invoices'], (prev) => {
      if (!prev?.data) return prev;
      const data = Array.isArray(prev.data) ? prev.data : [];
      const exists = data.some((i) => i._id === updated._id);
      return {
        ...prev,
        data: exists
          ? data.map((i) => (i._id === updated._id ? { ...i, ...updated } : i))
          : [updated, ...data],
      };
    });
  }, [qc]);

  // ── Filtres client (statut + recherche) ──────────────────────────────────────
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const client = inv.clientName || inv.client?.name || '';
        const matches =
          inv.invoiceNumber?.toLowerCase().includes(q) ||
          client.toLowerCase().includes(q) ||
          inv.clientPhone?.toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [invoices, filterStatus, searchQuery]);

  // ── Métriques (sur l'ensemble chargé) ─────────────────────────────────────────
  const metrics = useMemo(() => {
    const currency = invoices[0]?.currency || 'EUR';
    const paidAmount = invoices
      .filter((i) => i.status === 'paid')
      .reduce((s, i) => s + (Number(i.total) || 0), 0);
    const outstandingAmount = invoices
      .filter((i) => i.status === 'sent' || i.status === 'overdue')
      .reduce((s, i) => s + (Number(i.total) || 0), 0);
    const draftCount = invoices.filter((i) => i.status === 'draft').length;
    return { total: invoices.length, currency, paidAmount, outstandingAmount, draftCount };
  }, [invoices]);

  // ── Ouverture modale ──────────────────────────────────────────────────────────
  const openNew = useCallback(() => {
    setModalInvoice(null);
    setShowModal(true);
  }, []);

  const openInvoice = useCallback((invoice) => {
    setModalInvoice(invoice);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalInvoice(null);
  }, []);

  // ── Modèle de facture global ──────────────────────────────────────────────────
  const openTemplate = useCallback(() => setShowTemplateModal(true), []);
  const closeTemplate = useCallback(() => setShowTemplateModal(false), []);
  const handleTemplateSaved = useCallback((b) => {
    setBranding(b);
    setShowTemplateModal(false);
  }, []);

  const handleSaved = useCallback((saved) => {
    if (saved?._id) applyLocalUpdate(saved);
    closeModal();
    // Recharge en arrière-plan pour récupérer invoiceNumber / champs calculés
    qc.invalidateQueries({ queryKey: ['invoices'] });
  }, [applyLocalUpdate, closeModal, qc]);

  // ── Actions ───────────────────────────────────────────────────────────────────
  const runAction = useCallback(
    async (invoice, fn, { success, error: errMsg } = {}) => {
      if (!invoice?._id || busyId) return;
      setBusyId(invoice._id);
      try {
        const res = await fn(invoice._id);
        const updated = res?.data || res;
        if (updated?._id) applyLocalUpdate(updated);
        if (success) toast.success(success, { title: 'Facture' });
        return updated;
      } catch (err) {
        console.error('❌ Erreur action facture:', err);
        toast.error(err?.error?.message || err?.message || errMsg || 'Action impossible');
      } finally {
        setBusyId(null);
      }
    },
    [busyId, applyLocalUpdate, toast]
  );

  const handleSend = useCallback(
    (invoice) =>
      runAction(invoice, invoiceService.sendInvoice, {
        success: `Facture ${invoice.invoiceNumber || ''} envoyée`,
        error: 'Impossible d\'envoyer la facture',
      }),
    [runAction]
  );

  const handleMarkPaid = useCallback(
    (invoice) =>
      runAction(invoice, invoiceService.markPaid, {
        success: `Facture ${invoice.invoiceNumber || ''} marquée payée`,
        error: 'Impossible de marquer comme payée',
      }),
    [runAction]
  );

  const handleCancel = useCallback(
    (invoice) => {
      if (!window.confirm(`Annuler la facture ${invoice.invoiceNumber || ''} ?`)) return;
      return runAction(invoice, invoiceService.cancelInvoice, {
        success: `Facture ${invoice.invoiceNumber || ''} annulée`,
        error: 'Impossible d\'annuler la facture',
      });
    },
    [runAction]
  );

  // ── PDF : récupère le blob (authentifié) puis ouvre dans un onglet ─────────────
  const handleViewPdf = useCallback(
    async (invoice) => {
      if (!invoice?._id || busyId) return;
      setBusyId(invoice._id);
      try {
        const blob = await invoiceService.getInvoicePdf(invoice._id);
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
        // Libère l'URL objet après ouverture
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (err) {
        console.error('❌ Erreur PDF facture:', err);
        toast.error(err?.error?.message || err?.message || 'PDF indisponible (la facture doit être envoyée)');
      } finally {
        setBusyId(null);
      }
    },
    [busyId, toast]
  );

  // ── Envoi sur WhatsApp : réutilise le flux WhatsApp existant ───────────────────
  const handleSendWhatsApp = useCallback(
    async (invoice) => {
      const conversationId = getConversationId(invoice);
      if (!conversationId) {
        toast.info('Aucune conversation WhatsApp liée — ouvrez la messagerie pour l\'envoyer.');
        navigate('/whatsapp');
        return;
      }
      if (busyId) return;
      setBusyId(invoice._id);

      const client = invoice.clientName || invoice.client?.name || 'cher client';
      const link = getPaymentLink(invoice);
      const content =
        `Bonjour ${client}, voici votre facture ${invoice.invoiceNumber || ''} ` +
        `d'un montant de ${formatMoney(invoice.total, invoice.currency)}.` +
        (link ? `\nLien de paiement : ${link}` : '');

      try {
        // Même endpoint que la messagerie WhatsApp (réponse opérateur)
        await api.post(`/whatsflow/conversations/${conversationId}/reply`, {
          content,
          contentType: 'text',
          senderType: 'operator',
          isManualReply: true,
        });
        toast.success('Facture envoyée sur WhatsApp', { title: 'WhatsApp' });
      } catch (err) {
        console.error('❌ Erreur envoi WhatsApp:', err);
        toast.error(err?.error?.message || err?.message || 'Impossible d\'envoyer sur WhatsApp');
      } finally {
        setBusyId(null);
      }
    },
    [busyId, navigate, toast]
  );

  const handleViewConversation = useCallback(
    (invoice) => {
      const convId = getConversationId(invoice);
      if (convId) navigate(`/conversations?id=${convId}`);
    },
    [navigate]
  );

  const handlers = useMemo(
    () => ({
      onEdit: openInvoice,
      onSend: handleSend,
      onMarkPaid: handleMarkPaid,
      onCancel: handleCancel,
      onViewPdf: handleViewPdf,
      onSendWhatsApp: handleSendWhatsApp,
      onViewConversation: handleViewConversation,
    }),
    [openInvoice, handleSend, handleMarkPaid, handleCancel, handleViewPdf, handleSendWhatsApp, handleViewConversation]
  );

  // ── États chargement / erreur ───────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4" />
          <p className="text-gray-400">Chargement des factures...</p>
        </div>
      </div>
    );
  }

  if (isError && !invoicesData) {
    return (
      <div className="bg-red-500/10 border border-red-500 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-red-500" />
          <h3 className="text-red-500 font-semibold">Erreur</h3>
        </div>
        <p className="text-gray-400 mb-4">{error?.message}</p>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['invoices'] })}
          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <>
      <Factures
        invoices={filteredInvoices}
        metrics={metrics}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        onRefresh={() => qc.invalidateQueries({ queryKey: ['invoices'] })}
        isFetching={isFetching}
        onNew={openNew}
        onCustomizeTemplate={openTemplate}
        onRowClick={openInvoice}
        handlers={handlers}
        busyId={busyId}
      />

      <AnimatePresence>
        {showModal && (
          <FactureModal
            invoice={modalInvoice}
            branding={branding}
            onClose={closeModal}
            onSaved={handleSaved}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTemplateModal && (
          <InvoiceTemplateSettingsModal
            branding={branding}
            onClose={closeTemplate}
            onSaved={handleTemplateSaved}
          />
        )}
      </AnimatePresence>
    </>
  );
}
